/**
 * ``useRecentAlertsFeed`` — TanStack Query hook around the global
 * ``GET /api/query/recent-alerts?active_only=true`` endpoint
 * (REQ-QUERY-025), seeding the shared :func:`alerts/alerts-store` buffer.
 * The Alert sibling of :func:`detections/use-recent-detections-feed`
 * under the ADR-032 / ADR-029 cascade.
 *
 * This is the authoritative active-alerts source for the Alerts panel +
 * (PR5b) the Map alert-pin layer. It polls on a short interval so the
 * active window stays fresh; the live SSE merge
 * (:func:`alerts/use-alerts-sse-bridge`) sharpens the tail — both new
 * ``alert`` deltas and cross-session ``alert-dismissed`` removals —
 * between polls.
 *
 * On each successful fetch the hook upserts the response into the alerts
 * store (dedup by ``alert_id``; the store prunes by age + caps size). It
 * returns the query result so the panel can render loading / empty /
 * error states.
 *
 * Cross-references:
 *   - REQ-UI-062 (Alerts panel).
 *   - REQ-QUERY-025 (recent-alerts + dismiss surface).
 *   - REQ-AUTH-024 (operator role; live-map + alerts visibility).
 *   - ADR-024 §2 (Alerts panel); ADR-029 §6; ADR-032; storyboard Flow F.
 */
import { useEffect } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useSession } from "@/auth/session-context";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { Role } from "@/auth/session-types";
import type { components } from "@/lib/api-types.gen";
import { type AlertRecord, useAlertsStore } from "@/alerts/alerts-store";

export type RecentAlertsResponse = components["schemas"]["RecentAlertsResponse"];
type AlertItem = components["schemas"]["AlertItem"];

/**
 * Read roles that may see the live map + its alerts (mirrors the BFF
 * ``map_reader`` gate — same set as the recent-detections feed).
 */
export const RECENT_ALERTS_FEED_READER_ROLES: readonly Role[] = [
  "operator",
  "analyst",
  "admin",
  "support_engineer",
];

/** Poll cadence keeping the active window fresh between live SSE deltas. */
export const RECENT_ALERTS_FEED_REFETCH_MS = 10_000;

/** Result cap requested from the BFF. */
export const ALERT_FEED_LIMIT = 100;

export const RECENT_ALERTS_FEED_QUERY_KEY = ["recent-alerts-feed"] as const;

/** Map the wire :type:`AlertItem` to the store's :type:`AlertRecord`. */
export function alertRecordFromApi(item: AlertItem): AlertRecord {
  return {
    alert_id: item.alert_id,
    producer_id: item.producer_id,
    aggregator_id: item.aggregator_id,
    received_at: item.received_at,
    alert_type: item.alert_type,
    status: item.status,
    priority: item.priority,
    description: item.description,
    region_id: item.region_id,
    ranking: item.ranking,
    confidence: item.confidence,
    latitude_deg: item.latitude_deg,
    longitude_deg: item.longitude_deg,
    elevation_m: item.elevation_m,
    schema_version: item.schema_version,
    dismissed_at: item.dismissed_at,
    dismissed_by: item.dismissed_by,
    dismissal_note: item.dismissal_note,
    source_aggregator_id: item.source_aggregator_id,
    ingress_timestamp: item.ingress_timestamp,
    message_hash: item.message_hash,
    source_network: item.source_network ?? null,
  };
}

/**
 * Poll the global active recent-alerts endpoint and seed the alerts store.
 *
 * Safe to mount from multiple surfaces (the Alerts panel + (PR5b) the Map
 * pin layer): TanStack Query dedups concurrent observers of the shared
 * query key onto a single in-flight request + cache entry.
 */
export function useRecentAlertsFeed(): UseQueryResult<RecentAlertsResponse, ApiError> {
  const session = useSession();
  const enabled = session.hasAnyRole(RECENT_ALERTS_FEED_READER_ROLES);
  const query = useQuery<RecentAlertsResponse, ApiError>({
    queryKey: RECENT_ALERTS_FEED_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const raw = await apiFetch<unknown>(
        `/api/query/recent-alerts?active_only=true&limit=${ALERT_FEED_LIMIT}`,
        { signal },
      );
      if (!isResponseShape(raw)) {
        console.error(
          "[meridian/alerts/use-recent-alerts-feed] response failed shape validation",
          raw,
        );
        throw new ApiError(500, raw, "recent-alerts response did not match the expected shape");
      }
      return raw;
    },
    enabled,
    refetchInterval: RECENT_ALERTS_FEED_REFETCH_MS,
    staleTime: 0,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // Seed the shared buffer on each successful fetch. Keyed on the data
  // reference so it runs once per distinct response (TanStack returns a
  // stable reference for an unchanged cache entry).
  const data = query.data;
  useEffect(() => {
    if (data !== undefined) {
      useAlertsStore.getState().upsert(data.items.map(alertRecordFromApi));
    }
  }, [data]);

  return query;
}

function isResponseShape(value: unknown): value is RecentAlertsResponse {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return Array.isArray(obj.items) && typeof obj.duration_ms === "number";
}
